import React, { useState, useEffect, useRef } from 'react';
import { Form } from 'react-bootstrap';
import DepositorsService from '../../../services/DepositorsService';

const DepositorSearchSelect = ({
    value = '',
    onChange,
    companyId = null, // Required: Company ID to filter depositores
    placeholder = "Search and select depositor...",
    name = "depositorId",
    label = "Depositor",
    required = false,
    disabled = false,
    error = '',
    className = '',
    showLabel = true,
    allowClear = true,
    multiple = false,
    size = 'md' // sm, md, lg
}) => {
    const [options, setOptions] = useState([]);
    const [loading, setLoading] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const [isOpen, setIsOpen] = useState(false);
    const [filteredOptions, setFilteredOptions] = useState([]);
    const [selectedOption, setSelectedOption] = useState(null);
    const [selectedOptions, setSelectedOptions] = useState([]);
    const [dropdownPosition, setDropdownPosition] = useState('bottom');
    
    const dropdownRef = useRef(null);
    const inputRef = useRef(null);

    // Load  when companyId changes
    useEffect(() => {
        if (companyId) {
            loadDepositors();
        } else {
            // Clear options when no company is selected
            setOptions([]);
            setFilteredOptions([]);
            setSelectedOption(null);
            setSelectedOptions([]);
            setSearchTerm('');
        }
    }, [companyId]);

    // Handle value prop changes
    useEffect(() => {
        if (multiple) {
            const values = Array.isArray(value) ? value : (value ? [value] : []);
            const selected = options.filter(opt => opt.id === value) || [];
            setSelectedOptions(selected);
        } else {
            const selected = options.find(opt => opt.id === value) || null;
            setSelectedOption(selected);
            setSearchTerm(selected ? `${selected.name} (${selected.phoneNo || 'N/A'})` : '');
        }
    }, [value, options, multiple]);

    // Filter options based on search
    useEffect(() => {
        if (!searchTerm) {
            setFilteredOptions(options);
        } else {
            const filtered = options.filter(option =>
                option.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                (option.email && option.email.toLowerCase().includes(searchTerm.toLowerCase())) ||
                (option.phoneNo && option.phoneNo.toLowerCase().includes(searchTerm.toLowerCase()))
            );
            setFilteredOptions(filtered);
        }
    }, [searchTerm, options]);

    // Handle click outside
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
                setIsOpen(false);
                // Reset search term if no selection
                if (!multiple && !selectedOption) {
                    setSearchTerm('');
                } else if (!multiple && selectedOption) {
                    setSearchTerm(`${selectedOption.name} (${selectedOption.phoneNo || 'N/A'})`);
                }
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, [selectedOption, multiple]);

    const loadDepositors = async () => {
        if (!companyId) return;
        try {
            setLoading(true);
            const response = await DepositorsService.getDepositorsDropdown(companyId);
            if (response.success && response.data) {
                setOptions(response.data);
            }
        } catch (error) {
            console.error('Error loading depositor:', error);
            setOptions([]);
        } finally {
            setLoading(false);
        }
    };

    const handleInputChange = (e) => {
        const term = e.target.value;
        setSearchTerm(term);
        setIsOpen(true);
        
        // Clear selection if search is cleared
        if (!term && !multiple) {
            setSelectedOption(null);
            if (onChange) {
                const syntheticEvent = {
                    target: { name, value: null }
                };
                onChange(syntheticEvent);
            }
        }
    };

    const handleOptionSelect = (option) => {
        if (multiple) {
            const isSelected = selectedOptions.some(opt => opt.id === option.id);
            let newSelection;
            
            if (isSelected) {
                newSelection = selectedOptions.filter(opt => opt.id !== option.id);
            } else {
                newSelection = [...selectedOptions, option];
            }
            
            setSelectedOptions(newSelection);
            const values = newSelection.map(opt => opt.id);
            if (onChange) {
                const syntheticEvent = {
                    target: { name, value: values }
                };
                onChange(syntheticEvent);
            }
            setSearchTerm('');
        } else {
            setSelectedOption(option);
            setSearchTerm(`${option.name} (${option.phoneNo || 'N/A'})`);
            setIsOpen(false);
            if (onChange) {
                const syntheticEvent = {
                    target: { name, value: option.id }
                };
                onChange(syntheticEvent);
            }
        }
    };

    const handleRemoveOption = (optionToRemove) => {
        if (multiple) {
            const newSelection = selectedOptions.filter(opt => opt.id !== optionToRemove.id);
            setSelectedOptions(newSelection);
            const values = newSelection.map(opt => opt.id);
            if (onChange) {
                const syntheticEvent = {
                    target: { name, value: values }
                };
                onChange(syntheticEvent);
            }
        }
    };

    const handleClear = () => {
        if (multiple) {
            setSelectedOptions([]);
            if (onChange) {
                const syntheticEvent = {
                    target: { name, value: [] }
                };
                onChange(syntheticEvent);
            }
        } else {
            setSelectedOption(null);
            setSearchTerm('');
            if (onChange) {
                const syntheticEvent = {
                    target: { name, value: null }
                };
                onChange(syntheticEvent);
            }
        }
        setIsOpen(false);
    };

    const handleInputFocus = () => {
        if (!companyId) {
            return; // Don't open dropdown if no company is selected
        }
        
        setIsOpen(true);
        if (!multiple && selectedOption) {
            setSearchTerm('');
        }
        
        // Calculate dropdown position
        if (inputRef.current) {
            const rect = inputRef.current.getBoundingClientRect();
            const viewportHeight = window.innerHeight;
            const spaceBelow = viewportHeight - rect.bottom;
            const spaceAbove = rect.top;
            
            // If there's less than 250px below and more space above, show dropdown above
            if (spaceBelow < 250 && spaceAbove > spaceBelow) {
                setDropdownPosition('top');
            } else {
                setDropdownPosition('bottom');
            }
        }
    };

    const sizeClasses = {
        sm: 'form-control-sm',
        md: '',
        lg: 'form-control-lg'
    };

    // Determine if component should be disabled
    const isDisabled = disabled || !companyId;

    return (
        <div className={`depositor-search-select ${className}`} ref={dropdownRef} style={{ position: 'relative' }}>
            {showLabel && (
                <Form.Label className="form-label">
                    {label} {required && <span className="text-danger">*</span>}
                </Form.Label>
            )}
            
            <div className="position-relative" style={{ zIndex: isOpen ? 1040 : 'auto' }}>
                {/* Selected options for multiple select */}
                {multiple && selectedOptions.length > 0 && (
                    <div className="selected-options mb-2">
                        {selectedOptions.map((option) => (
                            <span key={option.id} className="badge bg-primary me-1 mb-1">
                                {option.label} ({option.city || 'N/A'})
                                <button
                                    type="button"
                                    className="btn-close btn-close-white ms-1"
                                    style={{ fontSize: '0.75em' }}
                                    onClick={() => handleRemoveOption(option)}
                                    disabled={isDisabled}
                                />
                            </span>
                        ))}
                    </div>
                )}
                
                {/* Search Input */}
                <div className="input-group">
                    <Form.Control
                        ref={inputRef}
                        type="text"
                        value={searchTerm}
                        onChange={handleInputChange}
                        onFocus={handleInputFocus}
                        placeholder={!companyId ? "Select a company first..." : placeholder}
                        disabled={isDisabled || loading}
                        className={`${sizeClasses[size]} ${error ? 'is-invalid' : ''}`}
                        autoComplete="off"
                    />
                    
                    {/* Clear Button */}
                    {allowClear && (multiple ? selectedOptions.length > 0 : selectedOption) && !isDisabled && (
                        <button
                            type="button"
                            className="btn btn-outline-secondary"
                            onClick={handleClear}
                            style={{ borderLeft: 'none' }}
                        >
                            <i className="fa fa-times"></i>
                        </button>
                    )}
                    
                    {/* Loading Spinner */}
                    {loading && (
                        <div className="input-group-text">
                            <div className="spinner-border spinner-border-sm" role="status">
                                <span className="visually-hidden">Loading...</span>
                            </div>
                        </div>
                    )}
                </div>
                
                {/* Dropdown Options */}
                {isOpen && !isDisabled && companyId && (
                    <div 
                        className="dropdown-menu show w-100 shadow-lg" 
                        style={{ 
                            maxHeight: '200px', 
                            minHeight: '50px',
                            overflowY: 'auto', 
                            overflowX: 'hidden',
                            zIndex: 1050,
                            position: 'absolute',
                            ...(dropdownPosition === 'top' ? {
                                bottom: '100%',
                                top: 'auto',
                                marginBottom: '2px'
                            } : {
                                top: '100%',
                                bottom: 'auto',
                                marginTop: '2px'
                            }),
                            left: 0,
                            right: 0,
                            border: '1px solid #dee2e6',
                            borderRadius: '0.375rem',
                            backgroundColor: '#fff',
                            boxShadow: '0 0.5rem 1rem rgba(0, 0, 0, 0.15)',
                            display: 'block',
                            padding: '0'
                        }}
                    >
                        {loading ? (
                            <div className="dropdown-item text-center py-3" style={{ 
                                margin: '0',
                                border: 'none',
                                backgroundColor: 'transparent'
                            }}>
                                <div className="spinner-border spinner-border-sm me-2" role="status"></div>
                                Loading depositors...
                            </div>
                        ) : filteredOptions.length > 0 ? (
                            <div style={{ 
                                maxHeight: '160px', 
                                overflowY: 'auto',
                                overflowX: 'hidden'
                            }}>
                                {filteredOptions.map((option) => {
                                    const isSelected = multiple 
                                        ? selectedOptions.some(opt => opt.id === option.id)
                                        : selectedOption?.id === option.id;
                                    
                                    return (
                                        <button
                                            key={option.id}
                                            type="button"
                                            className={`dropdown-item d-flex justify-content-between align-items-start py-2 px-3 ${isSelected ? 'active' : ''}`}
                                            onClick={() => handleOptionSelect(option)}
                                            style={{ 
                                                border: 'none',
                                                textAlign: 'left',
                                                whiteSpace: 'nowrap',
                                                overflow: 'hidden',
                                                textOverflow: 'ellipsis',
                                                width: '100%',
                                                backgroundColor: isSelected ? '#0d6efd' : 'transparent',
                                                color: isSelected ? '#fff' : '#212529'
                                            }}
                                        >
                                            <div className="flex-grow-1" style={{ 
                                                minWidth: 0,
                                                overflow: 'hidden'
                                            }}>
                                                <div className="fw-semibold" style={{ 
                                                    whiteSpace: 'nowrap',
                                                    overflow: 'hidden',
                                                    textOverflow: 'ellipsis'
                                                }}>
                                                    {option.name}
                                                </div>
                                                <small className={`d-block ${isSelected ? 'text-light' : 'text-muted'}`} style={{ 
                                                    whiteSpace: 'nowrap',
                                                    overflow: 'hidden',
                                                    textOverflow: 'ellipsis'
                                                }}>
                                                    {option.phoneNo ? `${option.phoneNo} • ` : ''}{option.email || 'N/A'}
                                                </small>
                                            </div>
                                            {isSelected && <i className="fa fa-check ms-2" style={{ flexShrink: 0 }}></i>}
                                        </button>
                                    );
                                })}
                            </div>
                        ) : (
                            <div className="dropdown-item text-muted text-center py-3" style={{ 
                                margin: '0',
                                border: 'none',
                                backgroundColor: 'transparent'
                            }}>
                                {searchTerm ? 'No depositors found' : 'No depositors available for this company'}
                            </div>
                        )}
                        
                        {/* Refresh Option */}
                        <div className="dropdown-divider" style={{ 
                            margin: '0.5rem 0',
                            borderTop: '1px solid #dee2e6'
                        }}></div>
                        <button
                            type="button"
                            className="dropdown-item text-primary py-2 px-3"
                            onClick={loadDepositors}
                            disabled={loading}
                            style={{ 
                                border: 'none',
                                textAlign: 'left',
                                backgroundColor: 'transparent',
                                width: '100%'
                            }}
                        >
                            <i className="fa fa-refresh me-2"></i>
                            Refresh Depositors
                        </button>
                    </div>
                )}
            </div>
            
            {/* Error Message */}
            {error && <div className="invalid-feedback d-block">{error}</div>}
            
            {/* Help Text */}
            {!error && (
                <Form.Text className="text-muted">
                    {!companyId 
                        ? 'Please select a company first to view depositors'
                        : multiple 
                            ? 'Type to search and select multiple depositors'
                            : 'Type to search and select a depositor'
                    }
                </Form.Text>
            )}
        </div>
    );
};

export default DepositorSearchSelect;